
import { useState, useEffect, createContext, useContext, useCallback } from 'react';

type ToastType = 'success' | 'error' | 'warning' | 'info';

interface ToastItem {
  id: number;
  message: string;
  type: ToastType;
}

interface ToastContextType {
  showToast: (message: string, type?: ToastType) => void;
}

const ToastContext = createContext<ToastContextType | null>(null);

let toastId = 0;

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const showToast = useCallback((message: string, type: ToastType = 'success') => {
    const id = ++toastId;
    setToasts(prev => [...prev, { id, message, type }]);

    // 3秒后自动移除
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, 3000);
  }, []);

  const removeToast = (id: number) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  };

  const getToastConfig = (type: ToastType) => {
    const configs = {
      success: {
        bg: 'bg-gradient-to-r from-emerald-500 to-teal-500',
        icon: 'ri-checkbox-circle-fill',
      },
      error: {
        bg: 'bg-gradient-to-r from-red-500 to-rose-500',
        icon: 'ri-close-circle-fill',
      },
      warning: {
        bg: 'bg-gradient-to-r from-amber-500 to-orange-500',
        icon: 'ri-alert-fill',
      },
      info: {
        bg: 'bg-gradient-to-r from-blue-500 to-indigo-500',
        icon: 'ri-information-fill',
      },
    };
    return configs[type];
  };

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      
      {/* Toast Container */}
      <div className="fixed top-6 right-6 z-[9999] flex flex-col gap-3 pointer-events-none">
        {toasts.map((toast) => {
          const config = getToastConfig(toast.type);
          return (
            <div
              key={toast.id}
              className={`${config.bg} text-white px-5 py-4 rounded-xl shadow-2xl flex items-center gap-3 min-w-[280px] max-w-[400px] pointer-events-auto animate-slide-in`}
              style={{
                animation: 'slideIn 0.3s ease-out',
              }}
            >
              <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                <i className={`${config.icon} text-2xl`}></i>
              </div>
              <p className="flex-1 text-sm font-medium leading-relaxed">{toast.message}</p>
              <button
                onClick={() => removeToast(toast.id)}
                className="w-6 h-6 flex items-center justify-center hover:bg-white/20 rounded-full transition-all cursor-pointer flex-shrink-0"
              >
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>
          );
        })}
      </div>

      <style>{`
        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateX(100px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
      `}</style>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
}

// 全局 toast 方法，用于非组件环境
let globalShowToast: ((message: string, type?: ToastType) => void) | null = null;

export function setGlobalToast(showToast: (message: string, type?: ToastType) => void) {
  globalShowToast = showToast;
}

export function toast(message: string, type: ToastType = 'success') {
  if (globalShowToast) {
    globalShowToast(message, type);
  } else {
    console.warn('Toast not initialized');
  }
}

export default { ToastProvider, useToast, toast, setGlobalToast };
